import { Component, computed, effect, inject, Signal } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { SectionComponent } from '../../../../components/layout/section/section.component';
import {
  EPage,
  DirectTicketConfigurationShoppingCartComponent,
} from '../../../../components/feedback/direct-ticket-configuration-shopping-cart/direct-ticket-configuration-shopping-cart.component';
import { Router } from '@angular/router';
import { ShoppingCartService } from '../../../../services/shopping-cart/shopping-cart.service';
import {
  EEntityType,
  ERelationType,
  IConfiguration,
  ICorporateDesign,
  INormalText,
  TariffProductDefinition,
  TariffProductGroup,
} from '@web/dtos';
import { SalutationStore } from '../../../../services/api/salutation/salutation-store.service';
import { ToTranslatedOptionPipe } from '../../../../pipes/to-select-option/to-translated-option.pipe';
import { CorporateDesignService } from '../../../../services/corporate-design/corporate-design.service';
import { ConfigurationStore } from '../../../../services/api/configuration/configuration-store.service';
import { DropDownComponent } from '../../../../components/data-input/form-inputs/drop-down/drop-down.component';
import { TextInputComponent } from '../../../../components/data-input/form-inputs/text-input/text-input.component';
import { CheckboxComponent } from '../../../../components/data-input/form-inputs/checkbox/checkbox.component';
import { SectionHeaderComponent } from '../../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../../components/layout/section/section-header/section-headline/section-headline.component';
import { DateInputComponent } from '../../../../components/data-input/form-inputs/date-input/date-input/date-input.component';
import { ITicketDefinitionForm } from '../../../../services/shopping-cart/shopping-cart-form.service';
import { LoadingAnimationComponent } from '../../../../components/feedback/loading-animation/loading-animation.component';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { WebTextStore } from '../../../../services/api/text/web-text-store.service';
import { ERoute } from '../../../../constants/routes.enum';
import { AuthProviderFactory } from '../../../../services/auth/auth-provider.factory';
import { AuthProvider } from '../../../../services/auth/auth.provider';
import { UserStore } from '../../../../services/api/user/user/user-store.service';

export interface ITicketPersonalDataForm {
  salutation: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  dateOfBirth: FormControl<Date | undefined>;
  email: FormControl<string>;
  agreement: FormControl<boolean>;
}

@Component({
  selector: 'aw-direct-ticket-personal-data-page',
  imports: [
    ReactiveFormsModule,
    SectionComponent,
    DirectTicketConfigurationShoppingCartComponent,
    DropDownComponent,
    TextInputComponent,
    CheckboxComponent,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    ToTranslatedOptionPipe,
    DateInputComponent,
    LoadingAnimationComponent,
    FilterTextsPipe,
    LanguagePipe,
  ],
  templateUrl: './direct-ticket-personal-data-page.component.html',
})
export class DirectTicketPersonalDataPageComponent {
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  private readonly router: Router = inject(Router);
  protected readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  protected readonly shoppingCartService: ShoppingCartService =
    inject(ShoppingCartService);
  readonly salutationsStore: SalutationStore = inject(SalutationStore);
  private readonly auth: AuthProvider =
    inject(AuthProviderFactory).getProvider('shop');
  private userStore: UserStore = inject(UserStore);

  protected readonly isLoading: Signal<boolean> = computed((): boolean => {
    return this.salutationsStore.isLoading();
  });
  protected readonly corporateDesign: Signal<ICorporateDesign> =
    this.corporateDesignService.corporateDesign;
  protected readonly configuration: Signal<IConfiguration[]> =
    this.configurationStore.configuration;
  protected readonly ticketConfiguration: Signal<
    TariffProductGroup | undefined
  > = this.shoppingCartService.directTicketConfiguration;
  protected readonly currentProduct: Signal<
    TariffProductDefinition | undefined
  > = this.shoppingCartService.currentProductDefinition;
  protected readonly salutationOptions: Signal<INormalText[]> =
    this.salutationsStore.salutationOptions;

  protected readonly productForms: FormArray<FormGroup<ITicketDefinitionForm>> =
    this.shoppingCartService.productForms;
  protected readonly personalDataForm: FormGroup<ITicketPersonalDataForm> =
    this.shoppingCartService.personalDataForm;

  protected readonly EPage: typeof EPage = EPage;
  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;

  constructor() {
    effect(() => {
      if (this.auth.isLoggedIn() && this.userStore.user()) {
        this.shoppingCartService.prefillPersonalData(this.userStore.user()!);
      }
    });
  }

  protected proceedToTicketOwnerData(): void {
    if (this.shoppingCartService.isTicketOwnerDataRequired()) {
      this.router.navigate([
        `/${ERoute.directTicketConfiguration}/${ERoute.ticketOwnerData}/`,
      ]);
    } else {
      this.router.navigate([
        `/${ERoute.directTicketConfiguration}/${ERoute.checkout}/`,
      ]);
    }
  }
}
